export const LEGACY_INVENTORY_TARGETS = [
  { id: 'GREG-GEAR', name: "Greg's Gear (legacy target)" },
  { id: 'LM-SET-01', name: 'Line Medic Set 01 (legacy target)' },
  { id: 'LM-SET-02', name: 'Line Medic Set 02 (legacy target)' },
  { id: 'LM-SET-03', name: 'Line Medic Set 03 (legacy target)' },
];

export const BARCODE_STORAGE_KEY = 'lm_inventory_barcode_map_v1';

export function parseInventoryQuantity(value, fallback = 0) {
  const text = String(value ?? '').trim();
  if (!text) return fallback;
  const match = text.match(/-?\d+(?:\.\d+)?/);
  if (!match) return fallback;
  const number = Number(match[0]);
  return Number.isFinite(number) ? number : fallback;
}

export function parseInventoryDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(String(value || '').trim());
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function normalizeInventoryBarcode(value) {
  return String(value || '').trim().replace(/\s+/g, '');
}

export function getBarcodeMap(storage) {
  if (!storage) return {};
  try {
    const parsed = JSON.parse(storage.getItem(BARCODE_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function rememberBarcode(storage, barcode, catalogId) {
  const normalizedBarcode = normalizeInventoryBarcode(barcode);
  const normalizedCatalogId = String(catalogId || '').trim();
  if (!storage || !normalizedBarcode || !normalizedCatalogId) return {};
  const next = { ...getBarcodeMap(storage), [normalizedBarcode]: normalizedCatalogId };
  storage.setItem(BARCODE_STORAGE_KEY, JSON.stringify(next));
  return next;
}

function inventoryState(record) {
  const state = String(record?.expirationState || '').trim().toUpperCase();
  if (['DATED', 'NA', 'UNKNOWN'].includes(state)) return state;
  return record?.expirationDate ? 'DATED' : 'UNKNOWN';
}

export function buildInventoryReconciliation(catalog, expirations, targetId, warningDays = 180, now = new Date()) {
  const targetRecords = (Array.isArray(expirations) ? expirations : [])
    .filter(record => String(record?.setId || '').trim() === String(targetId || '').trim() && !record?.returnedToLogistics);

  const warningCutoff = new Date(now);
  warningCutoff.setHours(23, 59, 59, 999);
  warningCutoff.setDate(warningCutoff.getDate() + Math.max(1, Number(warningDays) || 180));

  const rows = (Array.isArray(catalog) ? catalog : []).map(item => {
    const records = targetRecords.filter(record => String(record?.catalogId || '').trim() === String(item?.catalogId || '').trim());
    const required = Math.max(0, parseInventoryQuantity(item?.requiredQty, 1));
    const found = records.reduce((sum, record) => sum + Math.max(0, parseInventoryQuantity(record?.requiredQty, 0)), 0);
    const short = found < required;

    let expired = false;
    let dueSoon = false;
    let needsReview = false;

    records.forEach(record => {
      const state = inventoryState(record);
      if (state === 'UNKNOWN') needsReview = true;
      if (state !== 'DATED') return;
      const date = parseInventoryDate(record?.expirationDate);
      if (!date) {
        needsReview = true;
        return;
      }
      if (date < now) expired = true;
      else if (date <= warningCutoff) dueSoon = true;
    });

    if (!records.length) needsReview = false;

    return {
      ...item,
      required,
      found,
      records,
      short,
      expired,
      dueSoon,
      needsReview,
      blocker: short || expired || needsReview,
      complete: !short && !expired && !needsReview,
    };
  });

  const summary = {
    total: rows.length,
    complete: rows.filter(row => row.complete).length,
    short: rows.filter(row => row.short).length,
    expired: rows.filter(row => row.expired).length,
    dueSoon: rows.filter(row => row.dueSoon).length,
    needsReview: rows.filter(row => row.needsReview).length,
  };
  summary.blockers = rows.filter(row => row.blocker).length;
  summary.ready = rows.length > 0 && summary.blockers === 0;

  return { rows, summary };
}

export function makeInventoryRecord(target, catalogItem, existing, options = {}) {
  const useNewLot = Boolean(options.newLot);
  const timestamp = Date.now();
  const targetId = String(target?.id || '').trim();
  const targetName = String(target?.name || '').trim();
  const catalogId = String(catalogItem?.catalogId || '').trim();
  const recordId = useNewLot
    ? `${targetId}-${catalogId}-LOT-${timestamp}`
    : (existing?.recordId || `${targetId}-${catalogId}-PRIMARY`);

  return {
    recordId,
    setId: targetId,
    setName: targetName,
    catalogId,
    component: String(catalogItem?.component || '').trim(),
    item: String(catalogItem?.item || '').trim(),
    requiredQty: String(options.quantity ?? existing?.requiredQty ?? catalogItem?.requiredQty ?? '').trim(),
    expirationDate: options.noExpiration ? '' : String(options.expirationDate ?? existing?.expirationDate ?? '').trim(),
    expirationState: options.noExpiration
      ? 'NA'
      : (String(options.expirationDate ?? existing?.expirationDate ?? '').trim() ? 'DATED' : 'UNKNOWN'),
    lotSerial: String(options.lotSerial ?? existing?.lotSerial ?? '').trim(),
    returnedToLogistics: false,
    returnedDate: '',
    replacementDate: String(existing?.replacementDate || '').trim(),
    notes: String(options.notes ?? existing?.notes ?? '').trim(),
    updatedAt: '',
  };
}
